/**
 * The cantus firmus generator.
 *
 * A rejection-sampled random walk. Candidate melodies are cheap, so rather than
 * constructing a legal line the generator throws away everything that is not
 * idiomatic: start and end on the tonic, a single unrepeated climax in the
 * middle of the line, mostly steps, every leap recovered by a step back, no
 * repeated three-note group, and a penultimate note that approaches the final by
 * step.
 *
 * If 800 attempts fail — which they do not in practice — it falls back to Fux's
 * Dorian cantus firmus, transposed into the session key.
 */

import type { CfPosition, Edo, Exercise, MusicKey, RulesProfile } from './types';
import { dOf, leadingLetter, letterOf, midiInKey, pitch31Of, signatureAlteration } from './pitch';
import { isSolvable } from './legality';

/** Pick a uniformly random element. */
const pick = <T>(xs: readonly T[]): T => xs[Math.floor(Math.random() * xs.length)];

/** Fux's Dorian cantus firmus as scale steps above the tonic. */
const FUX_DORIAN = [0, 2, 1, 0, 4, 3, 5, 4, 3, 1, 0];

/**
 * Generate a cantus firmus in `key`, starting and ending on `tonicD`.
 * Returns diatonic indices, 8–11 measures long.
 */
export function generateCantusFirmus(key: MusicKey, tonicD: number): number[] {
  attempt: for (let attempt = 0; attempt < 800; attempt++) {
    const len = 8 + Math.floor(Math.random() * 4);
    const cf = [tonicD];
    const steps = [-1, 1];
    const leaps = [-4, -3, -2, 2, 3, 4];

    // The body: a constrained walk up to the cadence, which is spliced on after.
    for (let i = 1; i < len - 2; i++) {
      const prev = cf[i - 1];
      let next: number | null = null;
      for (let t = 0; t < 30; t++) {
        const move = Math.random() < 0.72 ? pick(steps) : pick(leaps);
        const cand = prev + move;
        if (cand === prev) continue;
        if (Math.abs(cand - tonicD) > 7) continue; // stay near home
        if (i >= 2 && Math.abs(prev - cf[i - 2]) > 2) {
          // The previous move was a leap: this one must step back the other way.
          const sameWay = Math.sign(cand - prev) === Math.sign(prev - cf[i - 2]);
          if (sameWay || Math.abs(cand - prev) > 1) continue;
        }
        const semi = Math.abs(midiInKey(cand, key) - midiInKey(prev, key));
        if (semi === 6 || semi > 12) continue; // no melodic tritone, no absurd leap
        next = cand;
        break;
      }
      if (next === null) continue attempt;
      cf.push(next);
    }

    // Cadence: step into the final from above or below (minor always from above,
    // so the raised leading tone does its work).
    const penultimate = tonicD + (key.mode === 'minor' ? 1 : pick([1, 1, -1]));
    const beforeCadence = cf[cf.length - 1];
    if (Math.abs(beforeCadence - penultimate) > 2 || beforeCadence === penultimate) continue;
    cf.push(penultimate, tonicD);

    if (!isIdiomatic(cf, key, tonicD)) continue;
    return cf;
  }
  return FUX_DORIAN.map((x) => tonicD + x);
}

/**
 * The rejection filter: range, climax, stepwise ratio, leap recovery, and the
 * absence of a repeated three-note group.
 */
function isIdiomatic(cf: readonly number[], key: MusicKey, tonicD: number): boolean {
  const hi = Math.max(...cf);
  const lo = Math.min(...cf);
  if (hi - lo > 9) return false;
  if (hi === tonicD) return false; // the line must actually rise somewhere
  if (cf.filter((d) => d === hi).length !== 1) return false;

  let stepCount = 0;
  for (let i = 1; i < cf.length; i++) {
    if (cf[i] === cf[i - 1]) return false; // no repeated notes at all
    if (Math.abs(cf[i] - cf[i - 1]) === 1) stepCount++;
  }
  if (stepCount / (cf.length - 1) < 0.55) return false;

  // Gran's ideals, honoured in both profiles because a better given melody
  // serves either dialect: the climax sits inside the line and makes a
  // consonance with scale degree 1.
  const hiPos = cf.indexOf(hi);
  if (hiPos < 2 || hiPos > cf.length - 3) return false;
  const ddHi = hi - tonicD;
  const semiHi = midiInKey(hi, key) - midiInKey(tonicD, key);
  if (![2, 3, 4, 5, 7].includes(ddHi) || semiHi % 12 === 6) return false;

  // Every leap — the cadence splice included — recovered by an opposite step.
  for (let i = 1; i < cf.length - 1; i++) {
    if (Math.abs(cf[i] - cf[i - 1]) > 2) {
      const recovery = cf[i + 1] - cf[i];
      if (Math.sign(recovery) === Math.sign(cf[i] - cf[i - 1]) || Math.abs(recovery) > 1) return false;
    }
  }

  // No three-note group may recur — that is what makes a line sound sequenced.
  const seen = new Set<string>();
  for (let i = 0; i + 2 < cf.length; i++) {
    const group = cf.slice(i, i + 3).join(',');
    if (seen.has(group)) return false;
    seen.add(group);
  }
  return true;
}

/**
 * Where the tonic sits, so both voices land comfortably on the chosen layout.
 * On a shared single staff the two registers are pulled together; on a grand
 * staff they spread out into their own clefs.
 */
export function tonicRegister(key: MusicKey, cfPos: CfPosition, singleStaff: boolean): number {
  const L = key.tonic;
  if (singleStaff) {
    return cfPos === 'lower'
      ? dOf(L, L === 'A' || L === 'B' ? 3 : 4)
      : dOf(L, L === 'A' || L === 'B' || L === 'F' || L === 'G' ? 4 : 5);
  }
  return cfPos === 'lower' ? dOf(L, L === 'A' || L === 'B' ? 2 : 3) : dOf(L, 4);
}

/**
 * Build a fresh exercise, regenerating until the position is provably
 * completable under the active rules — a student should never be handed a
 * cantus firmus with no legal answer.
 */
export function createExercise(
  key: MusicKey,
  cfPos: CfPosition,
  profile: RulesProfile,
  singleStaff: boolean,
  edo: Edo = 12,
): Exercise {
  const tonicD = tonicRegister(key, cfPos, singleStaff);
  for (let tries = 0; tries < 30; tries++) {
    const diatonicCf = generateCantusFirmus(key, tonicD);
    const cf = encodeCantus(diatonicCf, key, edo);
    const ex: Exercise = { edo, key, cfPos, cf, cp: cf.map(() => null) };
    if (isSolvable(ex, profile)) return ex;
  }
  const cf = encodeCantus(generateCantusFirmus(key, tonicD), key, edo);
  return { edo, key, cfPos, cf, cp: cf.map(() => null) };
}

/** Convert a generated diatonic melody into the exercise's stored pitch form. */
function encodeCantus(cf: readonly number[], key: MusicKey, edo: Edo): number[] {
  if (edo === 12) return cf.slice();
  return cf.map((d, measure) => {
    const letter = letterOf(d);
    let alteration = signatureAlteration(letter, key.sig);
    if (key.mode === 'minor' && measure === cf.length - 2 && letter === leadingLetter(key)) alteration++;
    return pitch31Of(d, alteration);
  });
}

/** Random choice helper exposed for the "surprise me" settings. */
export const randomOf = pick;
